'use client';

import * as React from 'react';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, Search, LogOut, Settings, HelpCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { AppThemeToggle } from '@/components/app-theme-toggle';

import { buildCrumbs } from '@/lib/nav';

const APP_ENV = import.meta.env.VITE_APP_ENV ?? 'production';

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-brand-navy text-[11px] font-semibold text-brand">
      {initials || 'U'}
    </span>
  );
}

export function TopBar({
  onOpenNav,
  onOpenCommand,
}: {
  onOpenNav: () => void;
  onOpenCommand: () => void;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const crumbs = buildCrumbs(pathname);
  const pageTitle = crumbs[crumbs.length - 1]?.label ?? 'Dashboard';
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border bg-surface/90 px-3 backdrop-blur-md sm:h-16 sm:gap-3 sm:px-4 md:px-6">
      <button
        type="button"
        onClick={onOpenNav}
        className="flex h-9 w-9 items-center justify-center rounded-lg text-text-muted hover:bg-surface-muted md:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="min-w-0 flex-1 md:hidden">
        <p className="truncate text-sm font-semibold text-brand-navy">{pageTitle}</p>
      </div>

      <nav aria-label="Breadcrumb" className="hidden min-w-0 flex-1 items-center gap-1.5 md:flex">
        <span className="text-[13px] font-semibold text-brand-navy">MPANGE</span>
        {crumbs.map((c, i) => (
          <span key={i} className="flex min-w-0 items-center gap-1.5">
            <span className="text-text-muted">/</span>
            {c.href ? (
              <Link to={c.href} className="truncate text-[13px] font-medium text-text-muted hover:text-brand">
                {c.label}
              </Link>
            ) : (
              <span className="truncate text-[13px] font-semibold text-brand">{c.label}</span>
            )}
          </span>
        ))}
      </nav>

      <div className="flex shrink-0 items-center justify-end gap-1 sm:flex-1 sm:gap-1.5 md:flex-none">
        {APP_ENV !== 'production' && (
          <span className="hidden rounded-md border border-warning/30 bg-warning-soft px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-warning sm:inline">
            {APP_ENV}
          </span>
        )}
        <button
          type="button"
          onClick={onOpenCommand}
          className="flex h-9 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-[13px] text-text-muted transition-colors hover:text-text sm:px-3"
          aria-label="Open search"
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">Search…</span>
          <kbd className="hidden rounded border border-border px-1.5 py-0.5 text-[11px] lg:inline">⌘K</kbd>
        </button>

        <AppThemeToggle />

        <button
          type="button"
          className="hidden h-9 w-9 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-muted hover:text-text sm:flex"
          aria-label="Help"
        >
          <HelpCircle className="h-[18px] w-[18px]" />
        </button>

        <div className="mx-1 hidden h-6 w-px bg-border sm:block" />

        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg p-0.5 pl-1 transition-colors hover:bg-surface-muted"
            aria-label="Account menu"
          >
            <Avatar name={user?.name ?? user?.email ?? 'User'} />
          </button>
          {menuOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-border bg-surface py-1 shadow-dropdown">
                <div className="px-3 py-2">
                  <p className="truncate text-[13px] font-semibold text-text">{user?.name ?? 'Account'}</p>
                  <p className="truncate text-[12px] text-text-muted">{user?.email}</p>
                </div>
                <div className="my-1 border-t border-border" />
                <Link
                  to="/dashboard/organization"
                  className="flex items-center gap-2 px-3 py-2 text-[13px] text-text hover:bg-surface-muted"
                  onClick={() => setMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" /> Organization
                </Link>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-[13px] text-danger hover:bg-danger-soft"
                  onClick={() => {
                    setMenuOpen(false);
                    void logout();
                    navigate('/login');
                  }}
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
